import { Box, IconButton, Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ReactElement, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

type RecordId = number | string;

const STORAGE_KEY = 'palo.list.order';

/**
 * Remembers the order of the list that was open last.
 *
 * One slot, not one per list: what the arrows in a mask walk through is the
 * list one came from, and there is only ever one of those. Private windows
 * refuse the store, in which case the arrows simply do not appear.
 */
export const rememberPaloListOrder = (ids: RecordId[]): void => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // No session storage: the mask does without the arrows.
    }
};

const readOrder = (): RecordId[] => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;

        return Array.isArray(parsed) ? (parsed as RecordId[]) : [];
    } catch {
        return [];
    }
};

export interface PaloRecordPagerProps {
    /** The record on screen; it has to be in the remembered list to page. */
    currentId?: RecordId;
    /** Opens another record of the same list. */
    onOpen: (id: RecordId) => void;
    previousLabel?: string;
    nextLabel?: string;
}

/**
 * Arrows through the records of the list one came from.
 *
 * Checking ten orders in a row meant ten times back to the list and ten times
 * finding the next line. ERPNext has the same two arrows in its header, and
 * they answer "where am I in this list" without loading the breadcrumb with a
 * path (#2206).
 */
export const PaloRecordPager = ({
    currentId,
    onOpen,
    previousLabel = 'Vorheriger Datensatz',
    nextLabel = 'Nächster Datensatz',
}: PaloRecordPagerProps): ReactElement | null => {
    const [order, setOrder] = useState<RecordId[]>([]);

    useEffect(() => setOrder(readOrder()), [currentId]);

    if (undefined === currentId || order.length < 2) {
        return null;
    }

    // The list holds numbers, the route a string; compare them as written.
    const index = order.findIndex(id => String(id) === String(currentId));

    if (index < 0) {
        return null;
    }

    const previous = index > 0 ? order[index - 1] : undefined;
    const next = index < order.length - 1 ? order[index + 1] : undefined;

    const arrow = (id: RecordId | undefined, label: string, icon: ReactElement): ReactElement => (
        <Tooltip title={label}>
            <Box component="span">
                <IconButton
                    size="small"
                    aria-label={label}
                    disabled={undefined === id}
                    onClick={() => undefined !== id && onOpen(id)}
                    sx={{ padding: '2px' }}
                >
                    {icon}
                </IconButton>
            </Box>
        </Tooltip>
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', color: tokens.color.ink2 }}>
            {arrow(previous, previousLabel, <ChevronLeft fontSize="small" />)}
            {arrow(next, nextLabel, <ChevronRight fontSize="small" />)}
        </Box>
    );
};
